'use client';

import Link from 'next/link';

export interface SimilarEntry {
  type: string;
  id: number;
  name: string;
  snippet: string;
  score: number;
}

interface Props {
  results: SimilarEntry[];
}

const typeConfig: Record<string, { badge: string; border: string; href: (id: number) => string; label: string }> = {
  character: {
    badge:  'bg-purple-900/60 text-purple-300',
    border: 'border-purple-500/20 hover:border-purple-500/40',
    label:  'Character',
    href:   id => `/characters/${id}`,
  },
  location: {
    badge:  'bg-blue-900/60 text-blue-300',
    border: 'border-blue-500/20 hover:border-blue-500/40',
    label:  'Location',
    href:   id => `/locations/${id}`,
  },
  lore: {
    badge:  'bg-emerald-900/60 text-emerald-300',
    border: 'border-emerald-500/20 hover:border-emerald-500/40',
    label:  'Lore',
    href:   id => `/lore/${id}`,
  },
};

export default function SemanticSidebar({ results }: Props) {
  return (
    <div className="w-64 shrink-0 space-y-3">
      <h3 className="text-xs font-mono uppercase tracking-widest text-slate-500 pb-1 border-b border-slate-800">
        Related World Entries
      </h3>

      {results.length === 0 ? (
        <p className="text-xs text-slate-600 italic leading-relaxed pt-2">
          Start writing to see related characters, locations, and lore...
        </p>
      ) : (
        results.map((entry, i) => {
          const cfg = typeConfig[entry.type];
          if (!cfg) return null;
          return (
            <Link
              key={i}
              href={cfg.href(entry.id)}
              className={`block p-3 rounded-xl bg-slate-900/60 border transition-colors ${cfg.border}`}
            >
              <span className={`inline-block text-xs font-mono uppercase tracking-widest px-1.5 py-0.5 rounded mb-1.5 ${cfg.badge}`}>
                {cfg.label}
              </span>
              <div className="font-serif font-bold text-sm text-white leading-tight">{entry.name}</div>
              {entry.snippet && (
                <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">{entry.snippet}</p>
              )}
            </Link>
          );
        })
      )}
    </div>
  );
}
